/**
 * Format product names for Excel (pipe-separated).
 * Example: "MacBook Air | iPhone 17"
 */
export function formatProductNames(products = []) {
  if (!Array.isArray(products) || products.length === 0) return ''

  return products
    .map((item) => item?.product ?? item?.name ?? '')
    .filter(Boolean)
    .join(' | ')
}

/**
 * Format product quantities for Excel (pipe-separated, same order as names).
 * Example: "2 | 5"
 */
export function formatProductQuantities(products = []) {
  if (!Array.isArray(products) || products.length === 0) return ''

  return products
    .map((item) => {
      const qty = item?.quantity ?? item?.qty ?? item?.units ?? item?.count
      return qty != null && qty !== '' ? String(qty) : ''
    })
    .filter((qty) => qty !== '')
    .join(' | ')
}

/**
 * Format product unit values for Excel (pipe-separated, same order as names).
 * Example: "10 | 20"
 */
export function formatProductValues(products = []) {
  if (!Array.isArray(products) || products.length === 0) return ''

  return products
    .map((item) => {
      const val = item?.value ?? item?.price ?? item?.unitPrice
      return val != null && val !== '' ? String(val) : '0'
    })
    .join(' | ')
}

/**
 * Calculate total value of products in a lead (sum of quantity * value).
 */
export function calculateLeadTotalValue(products = []) {
  if (!Array.isArray(products) || products.length === 0) return 0

  return products.reduce((sum, item) => {
    const qty = Number(item?.quantity ?? item?.qty ?? item?.units) || 0
    const val = Number(item?.value ?? item?.price ?? item?.unitPrice) || 0
    return sum + qty * val
  }, 0)
}

/**
 * Escape a CSV cell (quotes values that contain commas, quotes, or newlines).
 */
export function escapeCsvCell(value) {
  const str = value == null ? '' : String(value)
  if (/[",\n\r]/.test(str)) {
    return `"${str.replace(/"/g, '""')}"`
  }
  return str
}

/**
 * Build a CSV string from an array of plain objects.
 */
export function rowsToCsv(rows = []) {
  if (!rows.length) return ''

  const headers = Object.keys(rows[0])
  const lines = [
    headers.map(escapeCsvCell).join(','),
    ...rows.map((row) =>
      headers.map((key) => escapeCsvCell(row[key])).join(','),
    ),
  ]

  return lines.join('\n')
}

/**
 * Download a CSV string as a file.
 */
export function downloadCsv(filename, csvContent) {
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  link.click()
  URL.revokeObjectURL(url)
}
